import * as api from '../../requests.js'
import { SUBROUTES } from '../../App'
import { CreateInstancesFromJson } from '../Wizard2/WizardClasses'
import { getGroupItems } from '../Wizard2/WizardActions'
import { Auditor } from './Auditor'
import debounce from 'lodash/debounce'
import x2js from 'x2js'
const xml2Json = new x2js()

export const populateDesignerSteps = (data, parsedWizard) => {
  if (!parsedWizard) {
    parsedWizard = CreateInstancesFromJson(data)
  }
  return {
    type: 'POPULATE_DESIGNER_STEPS',
    stepOrder: parsedWizard.stepOrder,
    steps: parsedWizard.steps,
    sections: parsedWizard.sections,
    fields: parsedWizard.fields,
  }
}

export const populateDesigner = (data, parsedWizard) => {
  if (!parsedWizard) {
    parsedWizard = CreateInstancesFromJson(data.STEPS_JSON)
  }
  return {
    type: 'POPULATE_DESIGNER',
    stepOrder: parsedWizard.stepOrder,
    steps: parsedWizard.steps,
    sections: parsedWizard.sections,
    fields: parsedWizard.fields,
    title: data.TITLE,
    formDefinitionId: data.FORM_DEFINITION_ID,
    category: data.CATEGORY,
    description: data.DESCR,
    lastModifiedDate: data.LAST_MODIFIED_DATE,
    lastModifiedName: data.LAST_MODIFIED_BY_NAME,
    sourceId: data.SOURCE_ID,
    abbreviation: data.ABBREVIATION ? data.ABBREVIATION : '',
    meta: xml2Json.xml2js(data.METADATA) ? xml2Json.xml2js(data.METADATA).METADATA : {},
    mode: data.MODE,
    docTitleSrc: data.DOC_TITLE_SRC ? data.DOC_TITLE_SRC : '',
    docId: data.DOC_ID ? data.DOC_ID : '',
    formTitle: data.FORM_TITLE ? data.FORM_TITLE : '',
    internalReviewRequired: data.INTERNAL_REVIEW_REQUIRED_YN,
    onlyRenderChangedFieldsOnForm: data.ONLY_SHOW_CHANGED_FIELDS_ON_FORM_YN,
    noLoginRequired: data.PUBLIC_YN,
    notAvailableOnline: data.NOT_AVAILABLE_ONLINE_YN,
    requireStaffEntry: data.REQUIRE_STAFF_ENTRY_YN,
    showSignatures: data.SHOW_SIGNATURES,
    feeDescription: data.FEE_DESCR,
    webUserAccessLevelTypeId: data.PX_WUAL_TYPE_ID,
    webUserAccessLevelAfterActivation: data.AX_WUAL_TYPE_ID,
    draftsDisabled: data.DRAFTS_DISABLED,
    backOfficeOnly: data.BACK_OFFICE_ONLY_YN ? 'SOSStaff' : 'Public',
    globalVariables: data.GLOBAL_VARIABLES ? data.GLOBAL_VARIABLES : undefined,
    wizardAttributesLogic: data.WIZARD_ATTRIBUTES_LOGIC ? data.WIZARD_ATTRIBUTES_LOGIC : undefined,
    miscellaneousModuleId: data.MiscellaneousModuleId || null,
  }
}

export const setMiscModuleId = (miscellaneousModuleId) => ({
  type: '@@ds/SET_MISC_MODULE_ID',
  miscellaneousModuleId,
})

export const setWizardCategory = (category) => ({
  type: '@@ds/SET_WIZARD_CATEGORY',
  category,
})

export const loadWizardById = (formDefinitionId, category, miscellaneousModuleId, callback) => (dispatch) => {
  dispatch(setDesignerLoading(true))
  const isMiscType = !!(SUBROUTES || []).find(r => r.parameter === category)?.IsMiscRecordsType || !!miscellaneousModuleId
  const loadFn = isMiscType ? api.LOAD_MISC_WIZARD : api.LOAD_WIZARD_BY_ID
  loadFn(formDefinitionId, (data) => {
    let parsedWizard = CreateInstancesFromJson(data.STEPS_JSON)
    dispatch(populateDesigner(data, parsedWizard))
    dispatch(populateServerOptionsCache(parsedWizard.fields))
    dispatch(auditWizard())
    if (callback) {
      callback()
    }
  })
}

const populateServerOptionsCache = (fields) => (dispatch) => {
  const fieldPromises = Object.values(fields)
    .filter(({ type }) => ['serverSelect', 'serverRadio'].includes(type))
    .map(
      ({ optionsSrc, subOptionsSrc }) =>
        new Promise((resolve, reject) => {
          getGroupItems(optionsSrc, subOptionsSrc).then((res) => {
            let options
            if (typeof res === 'object') {
              options = res.options
            } else {
              options = res
            }
            resolve({
              options,
              optionsSrc,
              subOptionsSrc,
            })
          })
        })
    )

  Promise.all(fieldPromises).then((values) => {
    const serverOptionsCache = values.reduce((obj, { options, optionsSrc, subOptionsSrc }) => {
      obj[`${optionsSrc}/${subOptionsSrc}`] = options
      return obj
    }, {})
    dispatch(setOptionsCache(serverOptionsCache))
  })
}

export const setDesignerLoading = (loading) => ({
  type: '@@ds/SET_LOADING',
  loading,
})

export const selectField = (stepIndex, sectionIndex, fieldIndex, fieldType) => ({
  type: 'SELECT_FIELD',
  stepIndex,
  sectionIndex,
  fieldIndex,
  fieldType,
})

export const selectOptionSubField = (
  stepIndex,
  sectionIndex,
  fieldIndex,
  fieldType,
  optionIndex,
  subFieldIndex
) => ({
  type: 'SELECT_OPTION_SUB_FIELD',
  stepIndex,
  sectionIndex,
  fieldIndex,
  fieldType,
  optionIndex,
  subFieldIndex,
})

export const selectCheckSubField = (
  stepIndex,
  sectionIndex,
  fieldIndex,
  fieldType,
  checkType,
  subFieldIndex
) => ({
  type: 'SELECT_CHECK_SUB_FIELD',
  stepIndex,
  sectionIndex,
  fieldIndex,
  fieldType,
  checkType,
  subFieldIndex,
})

export const selectRepeaterSubField = (
  stepIndex,
  sectionIndex,
  fieldIndex,
  fieldType,
  subFieldIndex
) => ({
  type: 'SELECT_REPEATER_SUB_FIELD',
  stepIndex,
  sectionIndex,
  fieldIndex,
  fieldType,
  subFieldIndex,
})

export const select = ({ type, id }) => ({
  type: 'SELECT',
  selectType: type,
  id,
})

export const subSelect = ({ type, id }) => ({
  type: 'SUB_SELECT',
  selectType: type,
  id,
})

export const setActiveStep = (stepIndex) => ({
  type: 'SET_ACTIVE_STEP',
  stepIndex,
})

export const addStep = () => ({
  type: 'ADD_STEP',
})

export const addSection = () => ({
  type: 'ADD_SECTION',
})

export const addField = (sectionId, fieldClass, properties) => ({
  type: 'ADD_FIELD',
  sectionId,
  fieldClass,
  properties,
})

export const addFieldUnder = (fieldId, sectionId, fieldClass, properties) => ({
  type: 'ADD_FIELD_UNDER',
  fieldId,
  sectionId,
  fieldClass,
  properties,
})

export const addFieldAbove = (fieldId, sectionId, fieldClass, properties) => ({
  type: 'ADD_FIELD_ABOVE',
  fieldId,
  sectionId,
  fieldClass,
  properties,
})

export const togglePreview = (previewing) => ({
  type: 'TOGGLE_PREVIEW',
  previewing,
})

export const setFormDefinitionId = (formDefinitionId) => ({
  type: 'SET_FORM_DEFINITION_ID',
  formDefinitionId,
})

export const setCurrentRevisionId = (currentRevisionId) => ({
  type: 'SET_CURRENT_REVISION_ID',
  currentRevisionId,
})

export const loadWizardRevisionHistory =
  (formDefinitionId, callback, errorCallback) => (dispatch, getState) => {
    const { miscellaneousModuleId } = getState().designer.wizard
    const loadFn = miscellaneousModuleId ? api.GET_MISC_WIZARD_REVISION_HISTORY : api.LOAD_WIZARD_HISTORY_BY_ID
    let revisionList = []
    loadFn(
      formDefinitionId,
      (data) => {
        let jsonString = JSON.stringify(data)
        revisionList = JSON.parse(jsonString)
        let currentRevisionID = revisionList.sort((a, b) => b.REVISION_ID - a.REVISION_ID)[0]
          .REVISION_ID
        dispatch(setCurrentRevisionId(currentRevisionID))
        dispatch(setRevisionHistory(revisionList))
        if (callback) {
          callback(revisionList)
        }
      },
      errorCallback
    )
  }

export const setRevisionHistory = (revisionList) => ({
  type: 'SET_WIZARD_REVISION_HISTORY',
  revisionList,
})

export const saveWizard = (wizard, callback, errorCallback) => (dispatch, getState) => {
  const { miscellaneousModuleId } = getState().designer.wizard
  const onSuccess = (formDefinitionId) => {
    dispatch(setFormDefinitionId(formDefinitionId))
    dispatch(setDirty(false))
    callback(formDefinitionId)
  }
  if (miscellaneousModuleId != null) {
    api.SAVE_MISC_WIZARD(
      { ...wizard, MiscellaneousModuleId: miscellaneousModuleId },
      onSuccess,
      errorCallback
    )
  } else {
    api.SAVE_WIZARD(wizard, onSuccess, errorCallback)
  }
}

export const setDirty = (dirty) => ({
  type: 'SET_DIRTY',
  dirty,
})

export const resetDesignerState = () => ({
  type: 'RESET_DESIGNER_STATE',
})

export const toggleValidationEditor = (value) => ({
  type: 'TOGGLE_VALIDATION_EDITOR',
  value,
})

export const addOptionSubfield = ({ fieldId, optionIndex, fieldClass, properties, direction }) => ({
  type: 'ADD_OPTION_SUBFIELD',
  fieldId,
  optionIndex,
  fieldClass,
  properties,
  direction,
})

export const addCheckSubfield = ({ fieldId, checkType, fieldClass, properties, direction }) => ({
  type: 'ADD_CHECK_SUBFIELD',
  fieldId,
  checkType,
  fieldClass,
  properties,
  direction,
})

export const addRepeaterSubfield = ({ fieldId, fieldClass, properties, direction }) => ({
  type: 'ADD_REPEATER_SUBFIELD',
  fieldId,
  fieldClass,
  properties,
  direction,
})

export const addNestedFieldOption = (fieldId) => ({
  type: 'ADD_NESTED_FIELD_OPTION',
  fieldId,
})

export const reorderField = ({ direction, fieldId }) => ({
  type: 'REORDER_FIELD',
  direction,
  fieldId,
})

export const reorderStep = ({ direction, stepId, stepIndex }) => ({
  type: 'REORDER_STEP',
  direction,
  stepId,
  stepIndex,
})

export const recalculateStepOrder = ({ reorderedStep, displacedStep, placeBefore }) => ({
  type: 'RECALCULATE_STEP_ORDER',
  reorderedStep,
  displacedStep,
  placeBefore,
})

export const reorderSection = ({ direction, sectionId, sectionIndex }) => ({
  type: 'REORDER_SECTION',
  direction,
  sectionId,
  sectionIndex,
})

export const deleteSection = (sectionId) => ({
  type: 'DELETE_SECTION',
  sectionId,
})

export const deleteStep = (sectionId) => ({
  type: 'DELETE_SECTION',
  sectionId,
})

export const setHoverId = (hoverId) => ({
  type: 'SET_HOVER_ID',
  hoverId,
})

export const setAuditLogs = (auditLogs) => ({
  type: '@@ds/SET_AUDIT_LOGS',
  auditLogs,
})

export const setAuditError = (auditError) => ({
  type: '@@ds/SET_AUDIT_ERROR',
  auditError,
})

const audit = (dispatch, getState) => {
  const audit = new Auditor(getState().designer)
  audit.startAudit()
  dispatch(setAuditLogs(audit.logs))
}

const debouncedAudit = debounce(audit, 400, {
  leading: true,
})

export const auditWizard = () => (dispatch, getState) => {
  try {
    debouncedAudit(dispatch, getState)
  } catch (e) {
    console.error(e)
    dispatch(setAuditLogs([]))
    dispatch(setAuditError(true))
  }
}

export const setGlobalVariables = (globalVariables) => ({
  type: '@@ds/SET_GLOBAL_VARIABLES',
  globalVariables,
})

export const setOptionsCache = (serverOptionsCache) => ({
  type: '@@ds/SET_OPTIONS_CACHE',
  serverOptionsCache,
})

export const setOptionsCacheById = (id, options) => ({
  type: '@@ds/SET_OPTIONS_CACHE_BY_ID',
  id,
  options,
})
